import { useEffect } from "react";
import type { ReactNode } from "react";
import { offsetShadow, TYPE } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";

interface Props {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  width?: number;
  label?: string;
}

export function Modal({ open, onClose, children, width = 720, label }: Props) {
  const p = useTheme();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "rgba(0,0,0,0.55)",
        display: "grid",
        placeItems: "center",
        padding: 16,
        overflowY: "auto",
        animation: "fadeIn .15s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: p.card,
          color: p.fg,
          border: `3px solid ${p.border}`,
          boxShadow: offsetShadow(p, 10, 10),
          width: "100%",
          maxWidth: width,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          fontFamily: TYPE.body,
          overflowY: "auto",
        }}
      >
        {label && (
          <div
            style={{
              position: "absolute",
              top: -3,
              left: 24,
              transform: "translateY(-100%)",
              background: p.fg,
              color: p.bg,
              padding: "4px 10px",
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 1.5,
              fontFamily: TYPE.mono,
              border: `3px solid ${p.border}`,
              borderBottom: "none",
            }}
          >
            {label}
          </div>
        )}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 36,
            height: 36,
            background: p.bg,
            color: p.fg,
            border: `2px solid ${p.border}`,
            fontSize: 16,
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: TYPE.body,
            zIndex: 1,
          }}
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}
